import { z } from "zod";

import { ERROR_MSG } from "@/constants/ErrorMessage.constant";
import {
  basicValidator,
  dateValidator,
  numberValidator,
  singleSelectValidator,
} from "@/lib/validators";

export enum GroupType {
  DOCKET = "DOCKET",
  ESTIMATION = "ESTIMATION",
}

export const Items = z.object({
  title: basicValidator(true, ERROR_MSG.REQUIRED, 1),
  description: basicValidator(true, ERROR_MSG.REQUIRED, 1),
  unit: basicValidator(true, ERROR_MSG.REQUIRED, 1),
  defaultPrice: numberValidator({
    min: 1,
    maxChars: 10,
    messages: {
      min: ERROR_MSG.REQUIRED,
    },
  }),
  groupId: singleSelectValidator(false),
  priceChanges: z
    .array(
      z.object({
        startDate: dateValidator(true, "Start Date is required"),
        endDate: dateValidator(true, "End Date is required"),
        price: z.preprocess(
          (val) => {
            if (val === "" || val === null || val === undefined) {
              return undefined;
            }

            return parseFloat(val as string);
          },
          z.number().refine((val) => val !== undefined, {
            message: "Number is required",
          }),
        ),
      }),
    )
    .superRefine((data, ctx) => {
      const seen = new Map<string, number[]>();

      data.forEach((item, index) => {
        const startDate = item.startDate
          ? new Date(item.startDate).toISOString()
          : undefined;
        const endDate = item.endDate
          ? new Date(item.endDate).toISOString()
          : undefined;

        if (!startDate || !endDate) {
          return;
        }

        const key = `${startDate}-${endDate}`;

        if (seen.has(key)) {
          seen.get(key)?.push(index);
        } else {
          seen.set(key, [index]);
        }
      });

      // Add validation errors only to the repeated fields
      seen.forEach((indexes) => {
        if (indexes.length > 1) {
          indexes.forEach((idx) => {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Duplicate Start Date.",
              path: [idx, "startDate"], // Attach error only to repeated startDate field
            });

            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Duplicate End Date.",
              path: [idx, "endDate"], // Attach error only to repeated endDate field
            });
          });
        }
      });
    }),
});
export type TItems = z.infer<typeof Items>;

export const GroupSchema = z.object({
  name: z.string().trim().min(1, { message: "Required" }),
  description: z.string().trim().min(1, { message: "Required" }),
  type: z
    .union([z.nativeEnum(GroupType), basicValidator()])
    .refine((value) => value !== undefined, {
      message: "Required",
    }),
});

export type IGroupSchema = z.infer<typeof GroupSchema>;
